import React from 'react'
import type { GetServerSidePropsContext } from 'next'
import { Product } from '@mc/api'
import { Separator } from '@mc/design-system'
import { APIError } from '@mc/next-api-core'
import { paths } from '@mc/paths'
import { StaticDataProvider } from '@mc/static-data-react'
import { TRACK_PROPS } from '@mc/track-event'
import { TrackingParamsProvider } from '@mc/tracking-context'
import {
  CategoryNavCarousel,
  type Category as CarouselCategory,
} from 'components/CategoryNavCarousel'
import { RegularFooter } from 'components/Footer/RegularFooter'
import { CoreNavigation } from 'components/Navigation/consumer'
import { trackPen7565BackendEV } from 'experiments/pen-7565_v1_promo_banner_200_classes/server'
import { trackPen7584BackendEV } from 'experiments/pen-7584_v1_promo_banner_all_classes/server'
import { trackPen7680BackendEV } from 'experiments/pen-7680_v1_promo_banner_gift_cta/server'
import { articleCategoryPath } from 'libs/paths'
import {
  getArticleCategories,
  getArticles,
  getFeaturedArticleForCategory,
  getLatestArticlesForCategory,
  getArticlesInCategoryCount,
  getDefaultProduct,
} from 'libs/server/api'
import { looksLikeValidSlug } from 'libs/server/api/slug-validation'
import { getPageData } from 'libs/server/page-data'
import { pageErrorHandler } from 'libs/server/page-error-handler'
import { ALL_CLASSES, redirect } from 'libs/utils'
import { ArticleItem, ArticlesCount, FeaturedArticleItem } from 'types/data'
import { FeaturedArticle } from '../components/FeaturedArticle'
import StickyFooter from '../components/StickyFooter'
import { trackCategoryCarouselClick } from '../components/events'
import { Details } from './components/Details'
import { Meta } from './components/Meta'

type ArticleCategory = Awaited<ReturnType<typeof getArticleCategories>>[number]

interface ArticlesCategoriesProps {
  annualPass: Product
  articleCategories: ArticleCategory[]
  featuredArticle: FeaturedArticleItem
  hasCompletedQuiz: boolean
  latestArticles: Array<ArticleItem>
  shouldAutoOpenQuiz: boolean
  categorySlug: string
  category: ArticleCategory
  topArticles: Array<ArticleItem>
  totalArticles: ArticlesCount
}

const ArticlesCategories = ({
  annualPass,
  articleCategories,
  featuredArticle: { featuredArticle },
  latestArticles,
  category,
  categorySlug,
  topArticles,
  totalArticles: { count: totalLatestArticles },
}: ArticlesCategoriesProps) => (
  <StaticDataProvider
    value={{
      annualPass,
      articleCategories,
      featuredArticle,
      latestArticles,
      categorySlug,
      category,
      topArticles,
      totalLatestArticles,
    }}
  >
    <TrackingParamsProvider
      value={{ [TRACK_PROPS.PAGE_TYPE]: 'next-articles' }}
    >
      <Meta />
      <Separator className='mc-opacity--muted mc-m-0' />
      <div className='container'>
        <div className='mc-my-2'>
          <CategoryNavCarousel
            categories={articleCategories as CarouselCategory[]}
            pathCategory={articleCategoryPath}
            onNavClick={trackCategoryCarouselClick}
          />
        </div>
      </div>
      <div className='mc-theme-light mc-bg-light'>
        <FeaturedArticle
          featuredArticle={featuredArticle}
          heading={category.name || featuredArticle.category.name}
        />
        <Details
          categorySlug={categorySlug}
          latestArticles={latestArticles}
          totalLatestArticles={totalLatestArticles}
        />
      </div>
      <StickyFooter />
    </TrackingParamsProvider>
  </StaticDataProvider>
)

ArticlesCategories.pageSettings = {
  analyticsLabel: 'Articles Categories Landing',
  showPromo: true,
  navigation: CoreNavigation,
  footer: RegularFooter,
}

export const getServerSideProps = async (ctx: GetServerSidePropsContext) => {
  const slug = String(ctx.query.slug)

  if (!looksLikeValidSlug(slug)) {
    return pageErrorHandler(
      new APIError('/articles/categories/[slug]', 400, 'Invalid slug'),
    )
  }

  // instead of /articles/all-classes, redirect to /articles
  if (slug === ALL_CLASSES) {
    return redirect(ctx, paths.articles, { queryExclude: ['slug'] })
  }

  await trackPen7565BackendEV(ctx)
  await trackPen7584BackendEV(ctx)
  await trackPen7680BackendEV(ctx)

  return getPageData({
    criticalPromises: {
      articleCategories: getArticleCategories(ctx),
      featuredArticle: getFeaturedArticleForCategory(ctx, slug),
      latestArticles: getLatestArticlesForCategory(ctx, slug),
      topArticles: getArticles(ctx),
      totalArticles: getArticlesInCategoryCount(ctx, slug),
    },
    nonCriticalPromises: {
      annualPass: getDefaultProduct(ctx),
    },
    propsMapper: (props) => ({
      categorySlug: slug,
      category: (props.articleCategories as ArticleCategory[]).find(
        (category: ArticleCategory) => category.slug === slug,
      ),
      ...props,
    }),
  })
}

export default ArticlesCategories
